/**
 * SFE side of the DGI protocol annex SECEF BF ST 1.0 over a byte stream.
 *
 * - One command at a time: exchanges are queued (§3.1).
 * - A new SEQ per command, cycling 20h-FFh (§3.2).
 * - NAK or an unreadable reply: the same frame is sent again with the same
 *   SEQ (§3.3.1). No reply within the wait: the same frame is sent again with
 *   the same SEQ, which the MCF answers by repeating its last reply when it
 *   had already executed the command (§3.3.2).
 * - Each SYN byte restarts the wait, up to a busy-wait ceiling (§3.3.1).
 * - Replies whose SEQ or CMD differ from the pending command are dropped.
 *
 * `certifyInvoice` runs the §4.3 exchange: C1h → C3h → C0h → 31h… → 36h… →
 * 33h → 35h… → 38h. A failure before 38h cancels the invoice with 38h C, after
 * a 35h without DATA when none was sent (the DGI MCF accepts 38h C only after
 * a 35h); an invoice without accepted item is left to the next C3h, which
 * drops it. A 38h the MCF refuses is cancelled too. A 38h without a readable
 * reply raises `SecefOutcomeUnknownError` and sends nothing more.
 *
 * @module @efacture/mcf-protocol/secef-client
 */
import { randomInt } from "node:crypto";
import { DEFAULT_SECEF_DIALECT } from "./secef-dialect.js";
import { SECEF_CMD_ADD_ITEM, SECEF_CMD_CLIENT_INFO, SECEF_CMD_CLOSE_INVOICE, SECEF_CMD_EXTRA_INFO, SECEF_CMD_MCF_STATUS, SECEF_CMD_OPEN_INVOICE, SECEF_CMD_PAYMENT, SECEF_CMD_SERVER_STATUS, SECEF_CMD_SUBTOTAL, SECEF_CMD_TAXPAYER_INFO, SECEF_MIN_RESPONSE_WAIT_MS, SECEF_SEQ_MAX, SECEF_SEQ_MIN, buildSecefCommandFrame, decodeSecefStatus, readSecefResponse, } from "./secef-frame.js";
import { SECEF_38H_CANCELLED, decodeClientInfoResponse, decodeCloseInvoiceResponse, decodeExtraInfoResponse, decodeItemResponse, decodeMcfStatusResponse, decodeOpenInvoiceResponse, decodePaymentResponse, decodeServerStatusResponse, decodeSubtotalResponse, decodeTaxpayerInfoResponse, encodeClientInfoRequest, encodeCloseInvoiceRequest, encodeExtraInfoRequest, encodeItemRequest, encodeOpenInvoiceRequest, encodePaymentRequest, encodeTaxpayerInfoRequest, } from "./secef-commands.js";
import { SecefAmountMismatchError, SecefCertificationMismatchError, SecefCommandRejectedError, SecefOutcomeUnknownError, SecefSessionError, SecefTransportError, } from "./secef-errors.js";
import { toHundredths } from "./secef-fields.js";
export const DEFAULT_SECEF_RESPONSE_TIMEOUT_MS = 1_000;
export const DEFAULT_SECEF_MAX_BUSY_WAIT_MS = 30_000;
export const DEFAULT_SECEF_MAX_NAK_RETRIES = 3;
export const DEFAULT_SECEF_MAX_TIMEOUT_RETRIES = 2;
function normalizeCancelText(message) {
    return message.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().trim();
}
export class SecefClient {
    link;
    dialect;
    responseTimeoutMs;
    maxBusyWaitMs;
    maxNakRetries;
    maxTimeoutRetries;
    seq;
    rx = Buffer.alloc(0);
    onReceive = null;
    unsubscribe;
    exchangeTail = Promise.resolve();
    sessionTail = Promise.resolve();
    closed = false;
    warningSink = null;
    constructor(link, options = {}) {
        this.link = link;
        this.dialect = options.dialect ?? DEFAULT_SECEF_DIALECT;
        this.responseTimeoutMs = Math.max(options.responseTimeoutMs ?? DEFAULT_SECEF_RESPONSE_TIMEOUT_MS, SECEF_MIN_RESPONSE_WAIT_MS);
        this.maxBusyWaitMs = Math.max(options.maxBusyWaitMs ?? DEFAULT_SECEF_MAX_BUSY_WAIT_MS, this.responseTimeoutMs);
        this.maxNakRetries = options.maxNakRetries ?? DEFAULT_SECEF_MAX_NAK_RETRIES;
        this.maxTimeoutRetries = options.maxTimeoutRetries ?? DEFAULT_SECEF_MAX_TIMEOUT_RETRIES;
        const initial = options.initialSeq ?? randomInt(SECEF_SEQ_MIN, SECEF_SEQ_MAX + 1);
        if (!Number.isInteger(initial) || initial < SECEF_SEQ_MIN || initial > SECEF_SEQ_MAX) {
            throw new RangeError(`SEQ initial hors plage 20h-FFh : ${initial}`);
        }
        this.seq = initial;
        this.unsubscribe = link.onData((chunk) => {
            this.rx = Buffer.concat([this.rx, chunk]);
            this.onReceive?.();
        });
    }
    /** Stops listening to the link. Pending and later exchanges fail. */
    close() {
        this.closed = true;
        this.unsubscribe();
    }
    /** SEQ the next command will carry. */
    get nextSeq() {
        return this.seq;
    }
    /**
     * Sends one command and returns the MCF reply. Queued behind any exchange in
     * progress. Throws `SecefTransportError` when no usable reply arrives.
     */
    exchange(cmd, data = Buffer.alloc(0)) {
        const run = this.exchangeTail.then(() => this.runExchange(cmd, data));
        this.exchangeTail = run.catch(() => undefined);
        return run;
    }
    takeSeq() {
        const seq = this.seq;
        this.seq = seq >= SECEF_SEQ_MAX ? SECEF_SEQ_MIN : seq + 1;
        return seq;
    }
    async runExchange(cmd, data) {
        if (this.closed)
            throw new SecefSessionError("Client MCF fermé");
        const frame = buildSecefCommandFrame(this.takeSeq(), cmd, data, this.dialect);
        const seq = frame[2];
        let attempts = 0;
        let naks = 0;
        let timeouts = 0;
        for (;;) {
            this.rx = Buffer.alloc(0);
            attempts++;
            try {
                await this.link.write(frame);
            }
            catch (err) {
                throw new SecefTransportError(cmd, seq, "write", attempts, { cause: err });
            }
            const outcome = await this.waitReply(seq, cmd);
            if (outcome.kind === "frame") {
                return { seq, cmd, data: outcome.data, status: decodeSecefStatus(outcome.status), attempts };
            }
            if (outcome.kind === "busy")
                throw new SecefTransportError(cmd, seq, "busy", attempts);
            if (outcome.failure === "nak") {
                if (naks++ >= this.maxNakRetries)
                    throw new SecefTransportError(cmd, seq, "nak", attempts);
            }
            else if (timeouts++ >= this.maxTimeoutRetries) {
                throw new SecefTransportError(cmd, seq, "timeout", attempts);
            }
        }
    }
    waitReply(seq, cmd) {
        return new Promise((resolve) => {
            const busyDeadline = Date.now() + this.maxBusyWaitMs;
            let synSeen = 0;
            let timer;
            const finish = (outcome) => {
                if (timer)
                    clearTimeout(timer);
                this.onReceive = null;
                resolve(outcome);
            };
            const arm = () => {
                if (timer)
                    clearTimeout(timer);
                const remaining = busyDeadline - Date.now();
                if (remaining <= 0) {
                    finish({ kind: "busy" });
                    return;
                }
                timer = setTimeout(() => finish(synSeen > 0 && Date.now() >= busyDeadline ? { kind: "busy" } : { kind: "retry", failure: "timeout" }), Math.min(this.responseTimeoutMs, remaining));
            };
            const check = () => {
                for (;;) {
                    const result = readSecefResponse(this.rx);
                    switch (result.kind) {
                        case "incomplete":
                            if (result.synCount > synSeen) {
                                synSeen = result.synCount;
                                arm();
                            }
                            return;
                        case "nak":
                        case "malformed":
                            finish({ kind: "retry", failure: "nak" });
                            return;
                        case "frame": {
                            const { frame } = result;
                            this.rx = this.rx.subarray(frame.consumed);
                            synSeen = 0;
                            if (frame.seq === seq && frame.cmd === cmd) {
                                finish({ kind: "frame", data: frame.data, status: frame.status });
                                return;
                            }
                            continue;
                        }
                    }
                }
            };
            this.onReceive = check;
            arm();
            check();
        });
    }
    // ===========================================================================
    // Typed commands
    // ===========================================================================
    async command(cmd, data = Buffer.alloc(0)) {
        const reply = await this.exchange(cmd, data);
        this.warningSink?.(reply.status);
        if (reply.status.errors.length > 0) {
            const text = reply.data.toString("utf8");
            throw new SecefCommandRejectedError(cmd, reply.status, /^E/.test(text) ? text : "");
        }
        return reply;
    }
    /** C1h. */
    async readMcfStatus() {
        return decodeMcfStatusResponse((await this.command(SECEF_CMD_MCF_STATUS)).data, this.dialect);
    }
    /** C1h with the reply status bits. */
    async readMcfStatusReport() {
        const reply = await this.command(SECEF_CMD_MCF_STATUS);
        return { status: decodeMcfStatusResponse(reply.data, this.dialect), report: reply.status };
    }
    /** C2h. */
    async readServerStatus() {
        return decodeServerStatusResponse((await this.command(SECEF_CMD_SERVER_STATUS)).data, this.dialect);
    }
    /** 2Bh. */
    async readTaxpayerInfo(field) {
        const reply = await this.command(SECEF_CMD_TAXPAYER_INFO, encodeTaxpayerInfoRequest(field));
        return decodeTaxpayerInfoResponse(reply.data, this.dialect);
    }
    /** C3h. */
    async sendClientInfo(client) {
        const reply = await this.command(SECEF_CMD_CLIENT_INFO, encodeClientInfoRequest(client, this.dialect));
        const decoded = decodeClientInfoResponse(reply.data, this.dialect);
        if (!decoded.ok)
            throw new SecefCommandRejectedError(SECEF_CMD_CLIENT_INFO, reply.status, decoded.message);
        return reply.status;
    }
    /** C0h. */
    async openInvoice(request) {
        const reply = await this.command(SECEF_CMD_OPEN_INVOICE, encodeOpenInvoiceRequest(request, this.dialect));
        const decoded = decodeOpenInvoiceResponse(reply.data, this.dialect);
        if (!decoded.ok)
            throw new SecefCommandRejectedError(SECEF_CMD_OPEN_INVOICE, reply.status, decoded.message);
        return decoded;
    }
    /** 31h. */
    async addItem(item) {
        const reply = await this.command(SECEF_CMD_ADD_ITEM, encodeItemRequest(item, this.dialect));
        const decoded = decodeItemResponse(reply.data, this.dialect);
        if (!decoded.ok)
            throw new SecefCommandRejectedError(SECEF_CMD_ADD_ITEM, reply.status, decoded.message);
        return decoded;
    }
    /** 33h. */
    async readSubtotal() {
        return decodeSubtotalResponse((await this.command(SECEF_CMD_SUBTOTAL)).data);
    }
    /** 35h; returns MP. */
    async addPayment(payment) {
        const reply = await this.command(SECEF_CMD_PAYMENT, encodePaymentRequest(payment));
        const decoded = decodePaymentResponse(reply.data, this.dialect);
        if (!decoded.ok)
            throw new SecefCommandRejectedError(SECEF_CMD_PAYMENT, reply.status, decoded.message);
        return decoded.balance;
    }
    /** 36h read. */
    async readExtraInfo(field) {
        const reply = await this.command(SECEF_CMD_EXTRA_INFO, encodeExtraInfoRequest({ field }, this.dialect));
        const decoded = decodeExtraInfoResponse(reply.data, "read", this.dialect);
        return decoded.ok && decoded.mode === "read" ? decoded.content : "";
    }
    /** 36h write. */
    async writeExtraInfo(field, content) {
        const reply = await this.command(SECEF_CMD_EXTRA_INFO, encodeExtraInfoRequest({ field, content }, this.dialect));
        const decoded = decodeExtraInfoResponse(reply.data, "write", this.dialect);
        if (!decoded.ok)
            throw new SecefCommandRejectedError(SECEF_CMD_EXTRA_INFO, reply.status, decoded.message);
    }
    /** 38h with MV. */
    async closeInvoice(total) {
        const reply = await this.command(SECEF_CMD_CLOSE_INVOICE, encodeCloseInvoiceRequest({ kind: "certify", total }));
        const decoded = decodeCloseInvoiceResponse(reply.data, this.dialect);
        if (!decoded.ok)
            throw new SecefCommandRejectedError(SECEF_CMD_CLOSE_INVOICE, reply.status, decoded.message);
        return decoded;
    }
    /** 38h C. The MCF confirms with `E:ANNULEE`. */
    async cancelInvoice() {
        const reply = await this.command(SECEF_CMD_CLOSE_INVOICE, encodeCloseInvoiceRequest({ kind: "cancel" }));
        const decoded = decodeCloseInvoiceResponse(reply.data, this.dialect);
        if (decoded.ok) {
            throw new SecefSessionError("MCF : l'annulation a renvoyé une certification");
        }
        const text = normalizeCancelText(decoded.message);
        if (text !== SECEF_38H_CANCELLED && text !== "ANNULE") {
            throw new SecefCommandRejectedError(SECEF_CMD_CLOSE_INVOICE, reply.status, decoded.message);
        }
    }
    // ===========================================================================
    // Invoice session (§4.3)
    // ===========================================================================
    /**
     * Certifies one invoice. Sessions are serialized with each other; the
     * single-command methods above must not be used while a session runs.
     */
    certifyInvoice(session) {
        const run = this.sessionTail.then(() => this.runSession(session));
        this.sessionTail = run.catch(() => undefined);
        return run;
    }
    async runSession(session) {
        if (session.items.length === 0)
            throw new SecefSessionError("Facture sans article");
        const warnings = new Map();
        this.warningSink = (report) => {
            for (const flag of report.warnings)
                warnings.set(`${flag.byte}.${flag.bit}`, flag);
        };
        try {
            return { ...(await this.runSessionSteps(session)), warnings: [...warnings.values()] };
        }
        finally {
            this.warningSink = null;
        }
    }
    async runSessionSteps(session) {
        const expectedTotal = toHundredths(session.total, "total");
        const first = await this.readMcfStatusReport();
        if (first.report.invoiceOpen) {
            if (session.onOpenInvoice !== "cancel") {
                throw new SecefSessionError("MCF : une facture est déjà ouverte");
            }
            await this.addPayment(null).catch(() => undefined);
            await this.cancelInvoice();
        }
        await this.sendClientInfo(session.client);
        const opened = await this.openInvoice(session.invoice);
        let subtotal;
        const items = [];
        const paymentBalances = [];
        try {
            for (const item of session.items)
                items.push(await this.addItem(item));
            for (const info of session.extraInfo ?? [])
                await this.writeExtraInfo(info.field, info.content);
            subtotal = await this.readSubtotal();
            if (toHundredths(subtotal.total, "MV") !== expectedTotal) {
                throw new SecefAmountMismatchError(session.total, subtotal);
            }
            const payments = session.payments ?? [];
            if (payments.length === 0)
                paymentBalances.push(await this.addPayment(null));
            for (const payment of payments)
                paymentBalances.push(await this.addPayment(payment));
        }
        catch (err) {
            if (items.length > 0) {
                if (paymentBalances.length === 0)
                    await this.addPayment(null).catch(() => undefined);
                await this.cancelInvoice().catch(() => undefined);
            }
            throw err;
        }
        let certification;
        try {
            certification = await this.closeInvoice(session.total);
        }
        catch (err) {
            if (err instanceof SecefCommandRejectedError) {
                await this.cancelInvoice().catch(() => undefined);
                throw err;
            }
            throw new SecefOutcomeUnknownError(session.invoice.invoiceNumber, err);
        }
        const mismatches = [];
        if (certification.invoiceNumber !== session.invoice.invoiceNumber)
            mismatches.push("FN");
        if (certification.invoiceType !== session.invoice.invoiceType)
            mismatches.push("FT");
        if (certification.typeCounter !== opened.typeCounter)
            mismatches.push("FC");
        if (certification.totalCounter !== opened.totalCounter)
            mismatches.push("TC");
        if (certification.machineId !== first.status.machineId)
            mismatches.push("NIM");
        if (mismatches.length > 0) {
            throw new SecefCertificationMismatchError(certification, mismatches);
        }
        return {
            mcfStatus: first.status,
            opened,
            items,
            subtotal,
            paymentBalances,
            certification,
        };
    }
}
